import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../utils/apiConfig";
import Modal from "../../../components/shared/Modal";
import { ChevronLeftIcon, ChevronRightIcon, ClipboardIcon, CheckIcon } from "@heroicons/react/24/outline";
import { EnvelopeIcon, UserGroupIcon, UserIcon } from "@heroicons/react/24/outline";

import logger from '../../../utils/logger';

const ShareQuestionnaireModal = ({
  isOpen,
  onClose,
  householdMembers = [],
  householdId,
  fromDemo = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isJointResponse, setIsJointResponse] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [emails, setEmails] = useState({});
  const [shareableLink, setShareableLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Memoize the initial emails calculation
  const initialEmails = useMemo(() => {
    const emailMap = {};
    householdMembers.forEach(member => {
      if (member?.id && member?.email) {
        emailMap[member.id] = member.email;
      }
    });
    return emailMap;
  }, [householdMembers]);

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return;

    logger.debug('[ShareQuestionnaireModal] initializing state', {
      currentStep,
      householdMembers,
      fromDemo
    });

    // Only reset if step is not already 1
    setCurrentStep(prev => prev !== 1 ? 1 : prev);
    setIsJointResponse(true);
    setSelectedMembers([]);
    setError(null);
    setEmails(initialEmails);
    setShareableLink("");
    setCopySuccess(false);
    setMessage("");

    // Auto-select all members and populate emails for joint response
    const validMembers = householdMembers
      .filter((member) => member && member.id)
      .map((member) => member.id);
    setSelectedMembers(validMembers);

    // Pre-populate emails for all selected members
    const newEmails = { ...initialEmails };
    validMembers.forEach((memberId) => {
      const member = householdMembers.find((m) => m.id === memberId);
      if (member && member.email && !newEmails[memberId]) {
        newEmails[memberId] = member.email;
      }
    });
    setEmails(newEmails);
  }, [isOpen, initialEmails, householdMembers]);

  // Early return if modal is not open
  if (!isOpen) return null;

  const handleTypeChange = (isJoint) => {
    setIsJointResponse(isJoint);
    if (isJoint) {
      // Select all valid members and pre-populate their emails
      const validMembers = householdMembers
        .filter((member) => member && member.id)
        .map((member) => member.id);
      setSelectedMembers(validMembers);

      // Pre-populate emails for all selected members
      const newEmails = { ...emails };
      validMembers.forEach((memberId) => {
        const member = householdMembers.find((m) => m.id === memberId);
        if (member && member.email && !newEmails[memberId]) {
          newEmails[memberId] = member.email;
        }
      });
      setEmails(newEmails);
    } else {
      setSelectedMembers([]);
    }
  };

  const handleMemberSelect = (memberId) => {
    if (!memberId) return;

    // Convert memberId to number since select values are strings
    const memberIdNum = parseInt(memberId, 10);

    // Clear previous selection in individual mode
    setSelectedMembers([memberIdNum]);

    // Pre-populate email for the selected member if available
    const member = householdMembers.find((m) => m.id === memberIdNum);
    if (member && member.email) {
      setEmails({
        [memberIdNum]: member.email,
      });
    }
  };

  const handleEmailChange = (memberId, email) => {
    if (!memberId) return;
    setEmails((prev) => ({
      ...prev,
      [memberId]: email,
    }));
  };

  const handleMemberToggle = (memberId) => {
    if (!memberId) return;
    
    setSelectedMembers((prev) => {
      if (prev.includes(memberId)) {
        // Remove member if already selected
        return prev.filter(id => id !== memberId);
      } else {
        // Add member if not selected
        return [...prev, memberId];
      }
    });

    // If member is being added, pre-populate their email if available
    const member = householdMembers.find((m) => m.id === memberId);
    if (member && member.email && !emails[memberId]) {
      setEmails((prev) => ({
        ...prev,
        [memberId]: member.email,
      }));
    }
  };

  const handleGenerateLink = async () => {
    try {
      setError(null);

      if (!householdId) {
        throw new Error("Household ID is required");
      }

      // Filter out any invalid members before making the API call
      const validRecipients = selectedMembers
        .filter((memberId) => {
          const member = householdMembers.find((m) => m && m.id === memberId);
          return member && emails[memberId];
        })
        .map((memberId) => ({
          memberId,
          email: emails[memberId],
        }));

      if (fromDemo) {
        logger.debug('[Demo] Generating mock share link');
        setShareableLink('https://example.com/demo-questionnaire/123');
        setCurrentStep(3);
        return;
      }

      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/risk-questionnaire/share`,
        {
          recipients: validRecipients,
          isJointResponse,
        },
      );

      setShareableLink(response.data.shareableLink);
      setCurrentStep(3);
    } catch (error) {
      logger.error("Error generating share link:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to generate share link",
      );
    }
  };

  const handleCopyLink = () => {
    try {
      // Try using the modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(shareableLink)
          .then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
          })
          .catch((err) => {
            logger.error("Failed to copy using clipboard API:", err);
            fallbackCopyToClipboard(shareableLink);
          });
      } else {
        // Fallback for older browsers
        fallbackCopyToClipboard(shareableLink);
      }
    } catch (err) {
      logger.error("Error copying to clipboard:", err);
      fallbackCopyToClipboard(shareableLink);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Make it out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);

    // Focus and select the text
    textArea.focus();
    textArea.select();

    try {
      // Try to copy using the older execCommand method
      const successful = document.execCommand("copy");
      if (successful) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        logger.error("Failed to copy using execCommand");
      }
    } catch (err) {
      logger.error("Error copying using fallback method:", err);
    } finally {
      // Clean up
      document.body.removeChild(textArea);
    }
  };

  const handleSendInvites = async () => {
    try {
      if (fromDemo) {
        logger.debug('[Demo] Skipping send invites in demo mode');
        setShowConfirmation(true);
        return;
      }

      setError(null);

      if (!householdId) {
        throw new Error("Household ID is required");
      }

      const validRecipients = selectedMembers
        .filter((memberId) => {
          const member = householdMembers.find((m) => m && m.id === memberId);
          return member && emails[memberId];
        })
        .map((memberId) => ({
          memberId,
          email: emails[memberId],
        }));

      await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/risk-questionnaire/send`,
        {
          recipients: validRecipients,
          isJointResponse,
        },
      );

      setShowConfirmation(true);
    } catch (error) {
      logger.error("Error sending invites:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to send questionnaire invites",
      );
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      if (
        currentStep === 1 &&
        !isJointResponse &&
        selectedMembers.length === 0
      ) {
        return; // Don't proceed if no members selected in individual mode
      }
      if (currentStep === 2) {
        handleGenerateLink();
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const isNextDisabled = () => {
    if (currentStep === 1) {
      return !isJointResponse && selectedMembers.length === 0;
    }
    if (currentStep === 2) {
      return selectedMembers.some((memberId) => {
        const member = householdMembers.find((m) => m && m.id === memberId);
        return !member || !emails[memberId] || !emails[memberId].trim();
      });
    }
    return false;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Select Response Type
              </h3>
              <div className="space-y-4">
                <button
                  onClick={() => handleTypeChange(false)}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    !isJointResponse
                      ? "border-blue-500 bg-blue-50 dark:border-emerald-400 dark:bg-emerald-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Individual Response</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Send to a single household member
                  </p>
                </button>
                <button
                  onClick={() => handleTypeChange(true)}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    isJointResponse
                      ? "border-blue-500 bg-blue-50 dark:border-emerald-400 dark:bg-emerald-900/20 dark:shadow-[0_0_10px_rgba(52,211,153,0.3)]"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Joint Response</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Send to multiple household members
                  </p>
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                {isJointResponse ? "Select Household Members" : "Select Member"}
              </h3>
              <div className="space-y-2">
                {householdMembers.map((member) => (
                  member && member.id && (
                    <div key={member.id}>
                      <div className="relative">
                        <input
                          type="text"
                          value={member.email || emails[member.id] || ""}
                          readOnly
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600/50 rounded-md text-gray-900 dark:text-gray-300 pr-10"
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <input
                            type={isJointResponse ? "checkbox" : "radio"}
                            id={`member-${member.id}`}
                            name="member"
                            value={member.id}
                            checked={selectedMembers.includes(member.id)}
                            onChange={() =>
                              isJointResponse
                                ? handleMemberToggle(member.id)
                                : handleMemberSelect(member.id)
                            }
                            className="h-4 w-4 rounded-full text-emerald-400 focus:ring-1 focus:ring-emerald-400 focus:ring-offset-0 border-gray-500 bg-gray-700 checked:bg-emerald-400 checked:border-emerald-400 transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Share Questionnaire Link
              </h3>
              <div className="mt-2 flex rounded-md shadow-sm">
                <input
                  type="text"
                  value={shareableLink}
                  readOnly
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="relative inline-flex items-center space-x-2 px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 text-sm font-medium rounded-r-md text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {copySuccess ? (
                    <CheckIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ClipboardIcon className="h-5 w-5" />
                  )}
                  <span>{copySuccess ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const modalTitle = () => {
    switch (currentStep) {
      case 1:
        return "Share Risk Questionnaire";
      case 2:
        return isJointResponse ? "Select Household Members" : "Select Member";
      case 3:
        return "Share Questionnaire Link";
      default:
        return "Share Risk Questionnaire";
    }
  };

  const renderConfirmationModal = () => {
    const recipientCount = selectedMembers.length;
    const Icon = isJointResponse ? UserGroupIcon : UserIcon;
    
    return (
      <Modal
        isOpen={showConfirmation}
        onClose={handleConfirmationClose}
        title="Invites Sent Successfully"
        size="md"
      >
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-center">
            <div className="bg-emerald-100 dark:bg-emerald-400/20 rounded-full p-4">
              <EnvelopeIcon className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          
          <div className="text-center space-y-2">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100">
              Questionnaire Invites Sent!
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {recipientCount} {recipientCount === 1 ? 'invitation has' : 'invitations have'} been sent successfully.
            </p>
          </div>

          <div className="bg-white border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <div className="flex items-center space-x-3 text-gray-700 dark:text-gray-300">
              <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <span>
                {isJointResponse ? 'Joint Response Questionnaire' : 'Individual Response Questionnaire'}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-700 dark:text-gray-300">
                <EnvelopeIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <span>Recipients:</span>
              </div>
              <div className="ml-8 text-gray-700 dark:text-gray-300 space-y-1">
                <div>Thomas Borer <span className="text-sm text-gray-500 dark:text-gray-400">(thomas.borer@hotmail.com)</span></div>
                <div>Mary Borer <span className="text-sm text-gray-500 dark:text-gray-400">(mary.borer@yahoo.com)</span></div>
              </div>
            </div>
          </div>

          <button
            onClick={handleConfirmationClose}
            className="w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:border-emerald-400 dark:shadow-[0_0_10px_rgba(52,211,153,0.3)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
          >
            Continue
          </button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle()}
        size="lg"
      >
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {renderStepContent()}

          <div className="mt-6 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
            )}
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={isNextDisabled()}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:border-emerald-400 dark:shadow-[0_0_10px_rgba(52,211,153,0.3)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-emerald-400 transition-colors ${
                  isNextDisabled() ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Next
                <ChevronRightIcon className="h-5 w-5 ml-1" />
              </button>
            ) : (
              <button
                onClick={handleSendInvites}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:border-emerald-400 dark:shadow-[0_0_10px_rgba(52,211,153,0.3)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-emerald-400 transition-colors"
              >
                Send Invites
              </button>
            )}
          </div>
        </div>
      </Modal>
      {renderConfirmationModal()}
    </>
  );
};

export default ShareQuestionnaireModal;
